import { createElement } from 'react';
import { RefreshCw } from 'lucide-react';

import { toast } from '@/shared/components/toasts/PlatformToast';

export const RELEASE_UPDATE_TOAST_ID = 'platform-release-update';
export const RELEASE_UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

const RELEASE_UPDATE_CHECK_THROTTLE_MS = 60 * 1000;
const RELEASE_ACTIVATION_FALLBACK_MS = 4000;
const notifiedWorkers = new WeakSet<ServiceWorker>();

let initialized = false;
let reloadRequested = false;
let releaseUpdateCleanup: (() => void) | undefined;

const reportRegistrationFailure = (error: unknown) => {
    void import('@sentry/react')
        .then(({ captureException }) => captureException(error, { tags: { subsystem: 'release-update' } }))
        .catch(() => undefined);

    if (!import.meta.env.PROD) {
        console.debug('Service worker registration skipped.', error);
    }
};

const reloadApplication = () => {
    if (reloadRequested) {
        return;
    }

    reloadRequested = true;
    window.location.reload();
};

const activateRelease = (registration: ServiceWorkerRegistration, worker: ServiceWorker) => {
    if (registration.waiting !== worker) {
        reloadApplication();
        return;
    }

    let fallbackTimer = 0;
    const handleControllerChange = () => {
        window.clearTimeout(fallbackTimer);
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        reloadApplication();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange, { once: true });
    fallbackTimer = window.setTimeout(handleControllerChange, RELEASE_ACTIVATION_FALLBACK_MS);
    worker.postMessage({ type: 'SKIP_WAITING' });
};

export const notifyOfRelease = (registration: ServiceWorkerRegistration, worker = registration.waiting) => {
    if (!worker || notifiedWorkers.has(worker)) {
        return;
    }

    notifiedWorkers.add(worker);

    toast('New Version Available', {
        action: {
            label: 'Refresh Now',
            onClick: () => activateRelease(registration, worker),
        },
        actionsAlwaysVisible: true,
        description: 'A new release is ready. Refresh to load the latest features and fixes.',
        duration: Number.POSITIVE_INFINITY,
        icon: createElement(RefreshCw, { 'aria-hidden': true, size: '1rem' }),
        id: RELEASE_UPDATE_TOAST_ID,
    });
};

const observeInstallingWorker = (registration: ServiceWorkerRegistration, worker: ServiceWorker) => {
    const handleStateChange = () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
            notifyOfRelease(registration, worker);
        }
    };

    worker.addEventListener('statechange', handleStateChange);
    handleStateChange();

    return () => worker.removeEventListener('statechange', handleStateChange);
};

export const registerServiceWorkerForUpdates = async (): Promise<() => void> => {
    const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none',
    });
    const installationCleanups = new Set<() => void>();

    if (registration.waiting && navigator.serviceWorker.controller) {
        notifyOfRelease(registration, registration.waiting);
    }

    const handleUpdateFound = () => {
        if (!registration.installing) {
            return;
        }

        const cleanup = observeInstallingWorker(registration, registration.installing);
        installationCleanups.add(cleanup);
    };

    registration.addEventListener('updatefound', handleUpdateFound);

    let lastUpdateCheckAt = Date.now();
    const checkForUpdates = async (force = false) => {
        if (!navigator.onLine) {
            return;
        }

        const now = Date.now();
        if (!force && now - lastUpdateCheckAt < RELEASE_UPDATE_CHECK_THROTTLE_MS) {
            return;
        }

        lastUpdateCheckAt = now;

        try {
            await registration.update();
        } catch (error) {
            if (!import.meta.env.PROD) {
                console.debug('Release update check failed.', error);
            }
        }
    };
    const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            void checkForUpdates();
        }
    };
    const handleOnline = () => void checkForUpdates(true);
    const handlePageShow = () => void checkForUpdates();
    // Long-lived SPA sessions do not perform document navigations that would
    // otherwise prompt the browser to check the worker script automatically.
    const interval = window.setInterval(() => void checkForUpdates(true), RELEASE_UPDATE_CHECK_INTERVAL_MS);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
        window.clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('pageshow', handlePageShow);
        registration.removeEventListener('updatefound', handleUpdateFound);
        installationCleanups.forEach((cleanup) => cleanup());
        installationCleanups.clear();
    };
};

export const isServiceWorkerAvailable = () => 'serviceWorker' in navigator;

export const initServiceWorkerUpdates = () => {
    if (initialized || !import.meta.env.PROD || !isServiceWorkerAvailable()) {
        return;
    }

    initialized = true;

    const register = () => {
        void registerServiceWorkerForUpdates()
            .then((cleanup) => {
                releaseUpdateCleanup?.();
                releaseUpdateCleanup = cleanup;
            })
            .catch(reportRegistrationFailure);
    };

    if (document.readyState === 'complete') {
        register();
        return;
    }

    window.addEventListener('load', register, { once: true });
};
